<template>
    <div class="invoices-table" :class="{ 'table-on-modal': componentOnModal }">
            
        <SimpleInvoiceModal :invoice="modalInvoice" @close="onSimpleModalClose" />

        <FilterBar 
            :cookieName="(componentOnModal) ? modalTempCookie : filterOptions" 
            :class="{ 'no-totals': !showTotals }"
            filterBarType="invoices" 
            :skip-cookies="modalTempCookie"
            @change="filtersChanged" 
            :mainFilters="['SearchFilter', 'DateRangeFilter']"
            :secondaryFilters="secondaryFilters" 
            ref="invoices-table-filterbar">
            <template v-if="showTotals" v-slot:right-bar>
                <TotalAmountsInvoices :num-format="'euro'" :quotationAmounts="quotationsList" />
            </template>
        </FilterBar>

        <div class="content--inner">
            <overlay-scrollbars :options="scrollBarsOptions" class="invoices-table-scrollbar">
                <b-table
                primary-key="id"
                :class="['gk-table gk-table--responsive gk-table--striped gk-table--hover', { 'gk-table--small': componentOnModal, 'gk-table--statusmark': !selectableRows }]"
                :items="invoiceList"
                :fields="fields"
                :tbody-tr-class="rowClass"
                :key="tableKey"
                @row-clicked="rowClicked"
                :busy="loading"
                show-empty
                table-busy>
                <template #empty="">
                    <EmptyState :class="{ 'emptystate-simple': componentOnModal }">
                    <template v-if="!componentOnModal" v-slot:image>
                        <img src="/static/img/empty-states/empty-state-cost.png">
                    </template>
                    <template v-slot:title>
                        <span v-if="quotationsList">
                        {{ $gettext('We couldn\'t find any quotations') }}
                        </span>
                        <span v-else>
                        {{ $gettext('We couldn\'t find any invoices') }}
                        </span>
                    </template>
                    <template v-slot:content>
                        {{ $gettext('Start by creating a new one or try updating your filters.') }}
                    </template>
                    <template v-if="!componentOnModal" v-slot:cta>
                        <GekkoButton 
                        v-if="quotationsList"
                        :to="{ name: 'QuotationAdd' }"
                        :text="$gettext('Add a new quotation')"
                        icon="fa-solid fa-plus"
                        />
                        <GekkoButton
                        v-else 
                        :to="{ name: 'InvoiceAdd' }"
                        :text="$gettext('Add a new invoice')"
                        icon="fa-solid fa-plus"
                        />
                    </template>
                    </EmptyState>
                </template>
                <template #table-busy="">
                    <LoadingSpinner min-height />
                </template>

                <template v-if="selectableRows" #cell(selector)="row">
                    <b-form-checkbox v-model="row.item.selected" @change="selectedTableItem" />
                </template>

                <template #cell(invoice_date)="data">
                    <div class="row-status-indicator"></div>
                    <span v-if="data.item.correction" class="align-dates">{{ data.item.correction.timeframe }}</span>
                    <span v-else class="align-dates">{{ data.value | formatDate }}</span>
                </template>

                <template #cell(number)="data">
                    <span v-if="data.item.credited_invoice" class="">{{ data.value }} {{ $gettext('credited') }}</span>
                    <span v-else class="">
                    <span v-if="data.item.correction" class="">{{ data.item.correction.name }}</span>
                    <span v-else class="">{{ data.value }}</span>
                    </span>
                </template>

                <template #cell(customer)="data">
                    <span v-if="data.item.correction && data.item.correction.no_customer">---</span>
                    <div v-else>
                    <div class="d-block" v-if="data.item.recipient_customer"><strong>{{ data.item.recipient_customer.name }}</strong></div>
                    <span v-if="data.item.project">{{ data.item.project.name }}</span>
                    </div>
                </template>

                <template #cell(created_by)="data">
                    <div class="d-flex justify-content-center"><Avatar :user-id="data.item.created_by.id"></Avatar></div>
                </template>

                <template #cell(currency_total_amount)="data">
                    <!--
                    TODO test with different currency
                    -->
                    <span v-if="data.item.currency !== 'EUR' && data.item.status !== 'd'">
                    <div class="text-right">€ {{ data.item.total_amount | formatNumber }}</div>
                    <span v-if="data.item.total_amount === 0.0">
                        <GekkoButton
                            :to="{name: 'ControlPanelInvoiceEdit', params: {invoiceId: data.item.id}}"
                            :id="'tooltip-zeroamount-' + data.item.id"
                            :text="'(' + (data.item.total_amount | $options.filters.formatNumber) + ')'"
                            icon="fa-solid fa-triangle-exclamation"
                            variant="link text-danger"
                            class="inline"
                            v-b-tooltip.focus
                        />
                        <b-tooltip :target="'tooltip-zeroamount-' + data.item.id" triggers="hover">
                            <div v-if="isCompanyCountry('ES')" class="tooltip-text">
                            {{ $gettext('Set the total amount in EUR on the \'Add payment\' page to complete the invoice. The VAT report will only then take this into account.') }}
                            </div>
                            <div v-else class="tooltip-text">
                            {{ $gettext('Set the total amount in EUR on the \'Add payment\' page to complete the invoice.') }}
                            </div>
                        </b-tooltip>
                    </span>
                    <span v-else>({{ data.value | formatNumber }})</span>
                    </span>
                    <span v-else>
                    <div class="text-right">€ {{ data.value | formatNumber }}</div>
                    </span>
                </template>

                <template #cell(status)="data">
                    <div class="status-cell">
                    <div class="d-flex">
                        <div v-if="data.item.has_banking_transaction" class="status-circle-badge badge-blue" v-b-tooltip.hover :title="$gettext('Connected to a bank transaction')">
                        <font-awesome-icon icon="fa-solid fa-building-columns" />
                        </div>
                        <div v-if="data.item.created_via === 'woocommerce'" class="status-circle-badge badge-image" v-b-tooltip.hover :title="$gettext('Created via Woocommerce')">
                            <img src="/static/img/woocommerce2.png" />
                        </div>
                        <div v-if="data.item.credited_invoice" class="status-circle-badge badge-brown" v-b-tooltip.hover :title="$gettext('Credited')">
                        <font-awesome-icon icon="fa-solid fa-rotate-left" />
                        </div>
                        <div v-if="data.item.currency !== 'EUR'" class="status-circle-badge badge-blue" v-b-tooltip.hover :title="$gettext('Foreign currency')">
                        <font-awesome-icon icon="fa-solid fa-dollar-sign" />
                        </div>
                        <div v-if="data.item.display_status.error" class="status-circle-badge badge-danger" v-b-tooltip.hover :title="$gettext('Error sending the invoice')">
                        <font-awesome-icon icon="fa-solid fa-triangle-exclamation" />
                        </div>
                        <div v-if="!data.item.display_status.error && data.item.display_status.detailed_status === 'sending'" class="status-circle-badge badge-spinner" v-b-tooltip.hover :title="$gettext('Sending')">
                        </div>
                        <div v-if="data.item.uploaded || data.item.correction" class="status-circle-badge badge-blue" v-b-tooltip.hover :title="$gettext('Uploaded')">
                        <font-awesome-icon icon="fa-solid fa-cloud-arrow-up" />
                        </div>
                    </div>
                    <div :class="'status-badge status-' + data.item.display_status.detailed_status">
                        {{ data.item.display_status.display_status }}
                    </div> 
                    </div>
                </template>

                <template v-if="!componentOnModal" #cell(actions)="data">
                    <b-dropdown class="icon-toggle ui-dashboard-menu-dropdown" variant="link" right no-caret>
                    <template #button-content>
                        <font-awesome-icon icon="fa-solid fa-ellipsis-vertical" />
                    </template>

                    <b-dropdown-item v-if="!data.item.correction && !quotationsList" @click="downloadInvoice(data.item.id)">
                        <font-awesome-icon icon="fa-solid fa-download" />
                        <span v-translate>Download</span>
                    </b-dropdown-item>

                    <b-dropdown-item v-if="quotationsList" @click="downloadQuotation(data.item.id)">
                        <font-awesome-icon icon="fa-solid fa-download" />
                        <span v-translate>Download</span>
                    </b-dropdown-item>
                    
                    <b-dropdown-item v-if="data.item.correction" :to="{name: 'ControlPanelInvoiceEdit', params: {invoiceId: data.item.id}}">
                        <font-awesome-icon icon="fa-solid fa-pen-to-square" />
                        <span v-translate>Edit correction</span>
                    </b-dropdown-item>
                    
                    <b-dropdown-item 
                        variant="danger" 
                        v-gk-confirm="{
                        title: $gettext('Delete permanently?'),
                        message: deleteWarningText,
                        options: {
                            warning: (data.item.status !== 'd') ? sentInvoiceWarningText : null,
                            item: ((quotationsList) ? $gettext('QUOTATION') : $gettext('INVOICE')) + ' - ' + data.item.number,
                            variant: 'danger',
                            confirmText: $gettext('Delete permanently'),
                            safeguard: (data.item.status !== 'd'),
                            safeguardText: $gettext('I agree and understand the implications')
                        },
                        callback: (quotationsList) ? deleteQuotation : deleteInvoice,
                        callbackParams: data.item
                        }"
                    >
                        <font-awesome-icon icon="fa-regular fa-trash-can" />
                        <span v-if="!quotationsList" v-translate>Delete invoice</span>
                        <span v-if="quotationsList" v-translate>Delete quotation</span>
                    </b-dropdown-item>

                    </b-dropdown>
                </template>

                <template slot="bottom-row" v-if="nextUrl">
                    <td :colspan="fields.length" class="table-load-more">
                    <GekkoButton 
                        v-if="nextUrl && !fetchingData" 
                        @click="handleScroll()"
                        id="load_more_button" 
                        class="text-info"
                        variant="link" 
                        :text="$gettext('Load more invoices')" 
                    />
                    <LoadingSpinner v-if="fetchingData" />
                    </td>
                </template>
                </b-table>
            </overlay-scrollbars>
        </div>
    </div>
</template>
<script>
import { mapGetters } from "vuex"

import { promptDownload } from "@/utils/"

import Avatar from "@/components/Avatar"
import FilterBar from "@/components/list_filters/FilterBar"
import GekkoButton from "@/components/GekkoButton"
import InvoiceService from "@/store/services/invoice.service.js"
import QuotationService from "@/store/services/quotation.service.js"
import ChangeQuotationStatus from "@/components/invoices/ui/ChangeQuotationStatus";
import TotalAmountsInvoices from "@/components/invoices/ui/TotalAmountsInvoices"
import EmptyState from "@/components/ui/EmptyState"
import RecurrentInvoicesTable from "@/components/invoices/ui/RecurrentInvoicesTable"
import InvoiceCopyModal from "@/components/invoices/ui/InvoiceCopyModal"
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import SimpleInvoiceModal from "@/components/accountants/control_panel/SimpleInvoiceModal.vue"

export default {
    name: "AccountantInvoiceTable",
    components: { 
        GekkoButton, 
        Avatar, 
        FilterBar, 
        TotalAmountsInvoices, 
        EmptyState, 
        RecurrentInvoicesTable, 
        InvoiceCopyModal,
        LoadingSpinner,
        ChangeQuotationStatus,
        SimpleInvoiceModal
    },
    props: {
        data_object: Object,
        overlayScrollb: {
            type: Boolean,
            default: false
        }, 
        showTotals: {
            type: Boolean,
            default: true
        },
        showRecurring: {
            type: Boolean,
            default: true
        },
        componentOnModal: {
            type: Boolean,
            default: false
        },
        modalTempCookie: {
            type: String,
            default: null
        },
        selectableRows: {
            type: Boolean,
            default: false
        },
        disabledRows: {
            type: Array,
            default: () => []
        },
        forceFilters: {
            type: Object,
            default: () => {}
        },
        quotationsList: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            filters: {},
            fields: [
                { key: 'invoice_date', label: this.$gettext('Date'), sortable: false, tdClass: 'col-date width-125' },
                { key: 'number', label: this.$gettext('Invoice number'), sortable: false, tdClass: '' },
                { key: 'customer', label: this.$gettext('Customer'), sortable: false, tdClass: 'col-customer' },
                { key: 'created_by', label: this.$gettext('User'), sortable: false, tdClass: 'col-avatar' },
                { key: 'currency_total_amount', label: this.$gettext('Amount'), sortable: false, tdClass: 'col-amount', thClass: 'text-right' },
                { key: 'status', label: this.$gettext('Status'), sortable: false, tdClass: 'col-status', thClass: 'text-center' }
            ],
            invoiceList: [],
            customerList: {},
            projectList: {},
            nextUrl: '',
            totalNumber: 0,
            fetchingData: false,
            tableKey: 0,
            loading: true,
            scrollBarsOptions: {
                callbacks : {
                    onScrollStop: this.handleScroll
                }
            },
            invoiceToCopy: null,
            quotationToChange: null,
            modalInvoice: null
        }
    },
    computed: {
        ...mapGetters({
            currentCompany: "main/getCurrentCompany",
            creditableStatuses: 'invoices/getCreditableStatuses',
            getCompanyId: 'main/getCompanyId',
            isPremium: 'getIsPremium'
        }),
        selectedTableItems () {
            return (this.invoiceList) ? this.invoiceList.filter(item => item.selected).map((item) => item.id) : null
        },
        deleteWarningText () {
            return this.$gettext('You\'re about to delete this item permanently. This can\'t be undone.')
        },
        sentInvoiceWarningText () {
            return this.$gettext('Under Dutch law, it is prohibited to delete any invoices that have been sent and received by customers for services that have been delivered. In case you made a mistake in an invoice that has already been sent, you should not delete the incorrect invoice but send a new credit invoice, correcting your mistake.')
        },
        secondaryFilters () {  // Quotations not implemented for now
            if (this.quotationsList)
                return ['QuotationsCustomerFilter', 'QuotationsStatusFilter']
            else
                return ['InvoicesCustomerFilter', 'InvoicesStatusFilter']
        },
        filterOptions () {
            if (this.quotationsList)
                return 'QuotationsFilterOptionsVue2'
            else
                return 'InvoicesFilterOptionsVue2'
        }
    },
    watch: {
        invoiceList(newValue) {
            this.$store.commit('invoices/SET_TEMP_INVOICE_LIST', newValue);
        }
    },
    created () {
        if (this.selectableRows) {
            this.fields.unshift({ key: 'selector' })
        }
        if (!this.componentOnModal) {
            this.fields.push({ key: 'actions', label: this.$gettext('Actions'), sortable: false, tdClass: 'col-actions', thClass: 'text-center' })
        }
        window.addEventListener('scroll', this.handleScroll);
        this.$store.commit("filterStore/SET_OWNER_COMPANY", this.currentCompany.id);
        
        this.unsubscribe = this.$store.subscribe((mutation) => {
            if (mutation.type === 'invoices/REFRESH_INVOICE_LIST') {
                this.refreshData()
            }
        })
    },
    destroyed () {
        window.removeEventListener('scroll', this.handleScroll);
        if (this.componentOnModal) {
            this.$emit('destroyed')
        }
    },
    methods: {
        getInvoices (filters) {
            this.loading = true
            this.fetchingData = true

            filters.sender_company = this.getCompanyId
            filters.extended = true
            this.filters = filters
            this.filters = { ...this.filters, ...this.forceFilters }

            if (!this.quotationsList) {
                InvoiceService.list(this.filters).then((response) => {
                    this._processInvoiceList(response.data);
                })
            } else {
                QuotationService.list(this.filters).then((response) => {
                    this._processInvoiceList(response.data);
                });
            }
        },
        _processInvoiceList(invoiceList) {
            this.fetchingData = false
            this.invoiceList = invoiceList.results
            this.nextUrl = invoiceList.next
            this.totalNumber = invoiceList.count
            this.loading = false
            if (this.disabledRows) this.checkRows()
        },
        refreshData () {
            this.$refs['invoices-table-filterbar'].filtersChanged()
        },
        rowClicked (item) {
            if (!item.correction)
                this.modalInvoice = item
            else {
                this.$router.push({ name: 'ControlPanelInvoiceEdit', params: { invoiceId: item.id } })
            }
        },
        rowClass (item) {
            return (item) ? 'invoices-table-row ' + item.display_status.detailed_status : ''
        },
        selectedTableItem () {
            this.$emit('selected-item', this.selectedTableItems)
        },
        deleteInvoice (invoice) {
            InvoiceService.destroy(invoice.id).then(() => {
                this.onInvoiceDelete(invoice.id);
            })
        },
        deleteQuotation (quotation) {
            QuotationService.destroy(quotation.id).then((response) => {
                this.onInvoiceDelete(quotation.id);
            });
        },
        onInvoiceDelete (value) {
            for(var i in this.invoiceList){
                if(this.invoiceList[i].id === value){
                    this.invoiceList.splice(i, 1);
                    break;
                }
            }
        },
        filtersChanged (value) {
            this.getInvoices(value)
        },
        isInViewport (element_id) {
            var element = document.getElementById(element_id)
            if (!element) return false
            var rect = element.getBoundingClientRect();
            var html = document.documentElement;
            return (
                rect.top >= 0 &&
                rect.left >= 0 &&
                rect.bottom <= (window.innerHeight || html.clientHeight) &&
                rect.right <= (window.innerWidth || html.clientWidth)
            );
        },
        handleScroll () {
            if (!this.fetchingData && this.nextUrl) {
                const loadNewInvoices = this.isInViewport('load_more_button');
                if (loadNewInvoices) {
                this.fetchingData = true;
                InvoiceService.loadNextPage(this.nextUrl).then((response) => {
                    this.fetchingData = false;
                    this.nextUrl = response.data.next;
                    this.invoiceList = this.invoiceList.concat(response.data.results);
                    if (this.disabledRows) this.checkRows()
                })
                }
            }
        },
        checkRows () {
            for (var i = 0; i < this.invoiceList.length; i++) {
                if (this.disabledRows.includes(this.invoiceList[i].id)) {
                this.$set(this.invoiceList, i, { ...this.invoiceList[i], selected: true })
                }
            }
        },
        urlAddPayment (item_id) {
            return window.Urls['invoices_add_payment'](item_id)
        },
        isCompanyCountry (target_country) {
            return this.currentCompany.country === target_country
        },
        getSendText (status) {
            let status_text = ''
            switch (status) {
                case 'd':
                status_text = this.$gettext('Send invoice')
                break;
                case 's':
                status_text = this.$gettext('Send reminder')
                break;
                case 'p':
                status_text = this.$gettext('Send again')
                break;
            }
            return status_text
        },
        getUrl (url, item_id = null) {
            let target_url = null
            switch (url) {
                case 'invoices_send': 
                    target_url = '/invoices/s/#/send/' + item_id + '/'
                    break;
                default:
                    if(item_id) {
                        target_url = window.Urls[url](item_id)
                    } else {
                        target_url = window.Urls[url]()
                    }
                    break;
            }
            return target_url
        },
        redirectTo (url, item_id) {
            const target_url = this.getUrl(url, item_id)
            if (target_url) window.location.href = target_url
        },
        downloadInvoice(invoice_id) {
            InvoiceService.getInvoicePDFBlob(invoice_id).then(response => {
                promptDownload(response)
            }).catch(error => console.error('Error downloading PDF:', error));
        },
        downloadQuotation (quotation_id) {
            QuotationService.getQuotationPDFBlob(quotation_id).then(response => {
                promptDownload(response)
            }).catch(error => console.error('Error downloading PDF:', error));
        },
        creditInvoice (invoice_id) {
            InvoiceService.createCredit(invoice_id).then((response) => {
                window.location.href = window.Urls["invoices_edit_invoice"](response.data.id);
            })
        },
        copyInvoice (selected) {
            if (!this.isPremium) {
                this.$store.commit('togglePremiumModal', 'web_invoices_copy')
            } else {
                if (selected.customer_id) {
                // TODO backend: project_id?
                const options = { recipient_customer: selected.customer_id }
                if (selected.project_id) {
                    options.project = selected.project_id
                }
                InvoiceService.copyInvoice(this.invoiceToCopy.id, options).then((response) => {
                    this.invoiceToCopy = null
                    window.location.href = window.Urls["invoices_edit_invoice"](response.data.id)
                })
                }
            }
        },
        openViewInvoice (invoice_id) {
            if (!this.quotationsList)
                this.$router.push({ name: 'InvoiceView', params: { invoiceId: invoice_id } })
            else
                this.$router.push({ name: 'QuotationView', params: { quotationId: invoice_id } })
        },
        changeQuotationStatus(quotation) {
            this.quotationToChange = quotation;
        },
        onQuotationStatusChange() {
            this.getInvoices(this.filters);
        },
        onSimpleModalClose() {
            this.modalInvoice = null
        }
    }
}
</script>
<style lang="scss">
 // styles on InvoicesTable.vue (original component)
</style>
<style lang="scss" scoped>

</style>
